import { useState, createContext, useContext } from 'react';
import { formatBRL } from '../../lib/currency';

// Additional currencies for international display
export type AdminCurrency = 'BRL' | 'USD' | 'EUR';

const RATES: Record<AdminCurrency, number> = {
  BRL: 1,
  USD: 0.18,  // approximate, replace with live rate when Stripe FX API is connected
  EUR: 0.17,
};

const SYMBOLS: Record<AdminCurrency, string> = { BRL: 'R$', USD: '$', EUR: '€' };
const LOCALES: Record<AdminCurrency, string> = { BRL: 'pt-BR', USD: 'en-US', EUR: 'de-DE' };

export function formatAdminCurrency(amountBRL: number, currency: AdminCurrency): string {
  const converted = amountBRL * RATES[currency];
  return new Intl.NumberFormat(LOCALES[currency], {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(converted);
}

// ─── Context ─────────────────────────────────────────────────────────────────
interface CurrencyCtx {
  currency: AdminCurrency;
  setCurrency: (c: AdminCurrency) => void;
  fmt: (amountBRL: number) => string;
}

const Ctx = createContext<CurrencyCtx>({
  currency: 'BRL',
  setCurrency: () => {},
  fmt: (n) => formatBRL(n),
});

export function AdminCurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrency] = useState<AdminCurrency>('BRL');
  const fmt = (amountBRL: number) => formatAdminCurrency(amountBRL, currency);
  return <Ctx.Provider value={{ currency, setCurrency, fmt }}>{children}</Ctx.Provider>;
}

export function useAdminCurrency() {
  return useContext(Ctx);
}

/** Small toggle widget rendered next to financial page titles */
export function CurrencyToggle() {
  const { currency, setCurrency } = useAdminCurrency();
  return (
    <div style={{
      display: 'flex',
      gap: '2px',
      backgroundColor: 'var(--bg-elevated)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '6px',
      padding: '2px',
    }}>
      {(['BRL', 'USD', 'EUR'] as AdminCurrency[]).map(c => (
        <button
          key={c}
          onClick={() => setCurrency(c)}
          style={{
            padding: '3px 8px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: currency === c ? 700 : 400,
            background: currency === c ? 'rgba(31,78,95,0.2)' : 'transparent',
            color: currency === c ? 'var(--petrol)' : 'var(--text-muted)',
            transition: 'all 150ms',
          }}
        >
          {SYMBOLS[c]} {c}
        </button>
      ))}
      {currency !== 'BRL' && (
        <span style={{
          fontSize: '10px',
          color: 'var(--text-muted)',
          alignSelf: 'center',
          paddingLeft: '4px',
          fontStyle: 'italic',
          whiteSpace: 'nowrap',
        }}>
          ~taxa estimada
        </span>
      )}
    </div>
  );
}
